import { useEffect, useState } from "react";

import { isDevAuthBypass } from "./devAuthBypass";

export type HubOperationalDataSource = "supabase" | "simulation";

const HUB_DATA_SOURCE_STORAGE_KEY = "integrator_hub_data_source_v1";
const HUB_DATA_SOURCE_CHANGED_EVENT = "integrator-hub-data-source-changed";

function hubDevEnv(): Partial<ImportMetaEnv> {
  return (import.meta as ImportMeta & { env?: Partial<ImportMetaEnv> }).env ?? {};
}

function dispatchHubDataSourceChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(HUB_DATA_SOURCE_CHANGED_EVENT));
}

export function readHubOperationalDataSource(): HubOperationalDataSource {
  // Local clickable-UI testing always targets the simulation DB (no real Supabase data).
  if (isDevAuthBypass()) {
    return "simulation";
  }
  if (!hubDevEnv().DEV || typeof localStorage === "undefined") {
    return "supabase";
  }
  try {
    const raw = localStorage.getItem(HUB_DATA_SOURCE_STORAGE_KEY);
    return raw === "simulation" ? "simulation" : "supabase";
  } catch {
    return "supabase";
  }
}

export function setHubOperationalDataSource(next: HubOperationalDataSource): void {
  if (!hubDevEnv().DEV || typeof localStorage === "undefined") return;
  try {
    if (next === "supabase") {
      localStorage.removeItem(HUB_DATA_SOURCE_STORAGE_KEY);
    } else {
      localStorage.setItem(HUB_DATA_SOURCE_STORAGE_KEY, next);
    }
  } finally {
    dispatchHubDataSourceChanged();
  }
}

export function subscribeHubOperationalDataSource(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onChanged = (): void => listener();
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== HUB_DATA_SOURCE_STORAGE_KEY) return;
    listener();
  };
  window.addEventListener(HUB_DATA_SOURCE_CHANGED_EVENT, onChanged);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(HUB_DATA_SOURCE_CHANGED_EVENT, onChanged);
    window.removeEventListener("storage", onStorage);
  };
}

export function useHubOperationalDataSource(): HubOperationalDataSource {
  const [source, setSource] = useState<HubOperationalDataSource>(() => readHubOperationalDataSource());
  useEffect(() => subscribeHubOperationalDataSource(() => setSource(readHubOperationalDataSource())), []);
  return source;
}

export function hubOperationalDataSourceRequestHeader(): Record<string, string> {
  if (isDevAuthBypass()) return { "X-Hub-Data-Source": "simulation" };
  if (!hubDevEnv().DEV) return {};
  if (readHubOperationalDataSource() !== "simulation") return {};
  return { "X-Hub-Data-Source": "simulation" };
}

export function hubOperationalDataSourceLabel(source: HubOperationalDataSource): string {
  return source === "simulation" ? "Simulation (local Postgres)" : "Supabase dev";
}
